import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useIncidents } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { AlertTriangle, Plus, Clock, CheckCircle2, XCircle, Edit, Trash2, User, Eye, Network } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { LinkManager } from '@/components/shared/LinkManager';
import { EntityLinkedPreview } from '@/components/shared/EntityLinkedPreview';
import { IncidentEditDialog } from '@/components/incidents/IncidentEditDialog';
import { IncidentCreateDialog } from '@/components/incidents/IncidentCreateDialog';
import { useLinkStore } from '@/stores/linkStore';

export default function IncidentsPage() {
  const { organization, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterAssigned = searchParams.get('filter') === 'assigned';
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  
  const { loadLinks } = useLinkStore();

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const { incidents, isLoading, createIncident, updateIncident, deleteIncident, isDeleting } = useIncidents(organization?.id);
  
  const selectedIncident = incidents?.find(i => i.id === selectedIncidentId);

  const handleSubmit = async (data: any, links?: Array<{id: string; type: any; name: string}>) => {
    if (!data.title) {
      toast.error('Please enter a title');
      return;
    }
    
    if (!organization?.id) {
      toast.error('No organization found');
      return;
    }

    createIncident({
      ...data,
      org_id: organization.id,
    }, {
      onSuccess: async (newIncidentData: any) => {
        if (newIncidentData?.id && links && links.length > 0) {
          const { useLinkStore } = await import('@/stores/linkStore');
          const { bulkCreateLinks } = useLinkStore.getState();
          
          for (const link of links) {
            await bulkCreateLinks('incident', newIncidentData.id, link.type, [link.id]);
          }
        }
      }
    });
    setDialogOpen(false);
  };

  const filteredIncidents = (incidents || []).filter(incident => {
    if (filterAssigned && incident.owner_id !== profile?.user_id) return false;
    if (statusFilter !== 'all' && incident.status !== statusFilter) return false;
    if (priorityFilter !== 'all' && incident.priority !== priorityFilter) return false;
    return true;
  });

  const getPriorityBadge = (priority: string) => {
    const colors: Record<string, string> = {
      low: 'bg-blue-500/10 text-blue-500',
      medium: 'bg-yellow-500/10 text-yellow-500',
      high: 'bg-orange-500/10 text-orange-500',
      critical: 'bg-red-500/10 text-red-500',
    };
    return <Badge className={colors[priority] || colors.medium}>{priority}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-500/10 text-gray-500',
      investigating: 'bg-yellow-500/10 text-yellow-500',
      reported: 'bg-blue-500/10 text-blue-500',
      resolved: 'bg-green-500/10 text-green-500',
      closed: 'bg-gray-500/10 text-gray-500',
    };
    return <Badge className={colors[status] || colors.draft}>{status}</Badge>;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'resolved':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'closed':
        return <XCircle className="w-5 h-5 text-gray-500" />;
      case 'investigating':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertTriangle className="w-5 h-5 text-orange-500" />;
    }
  };

  const openDetailSheet = (incidentId: string) => {
    setSelectedIncidentId(incidentId);
    setDetailSheetOpen(true);
  };

  if (isLoading) {
    return <div>Loading incidents...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Incidents</h1>
          <p className="text-muted-foreground">Track and manage security incidents</p>
        </div>
        <Button onClick={() => setDialogOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Report Incident
        </Button>
      </div>

      <div className="flex gap-4 items-center">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="investigating">Investigating</SelectItem>
            <SelectItem value="reported">Reported</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>

        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant={filterAssigned ? "default" : "outline"}
          size="sm"
          onClick={() => {
            if (filterAssigned) {
              searchParams.delete('filter');
            } else {
              searchParams.set('filter', 'assigned');
            }
            setSearchParams(searchParams);
          }}
        >
          <User className="h-4 w-4 mr-2" />
          Assigned to me
        </Button>
      </div>

      <div className="space-y-4">
        {filteredIncidents.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No incidents found</h3>
              <p className="text-muted-foreground text-center mb-4">
                No incidents in this category. Track security incidents when they occur.
              </p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Report Your First Incident
              </Button>
            </CardContent>
          </Card>
        ) : (
          filteredIncidents.map((incident) => (
            <Card 
              key={incident.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => openDetailSheet(incident.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getStatusIcon(incident.status)}
                      <CardTitle className="text-lg">{incident.title}</CardTitle>
                      {getPriorityBadge(incident.priority)}
                      {getStatusBadge(incident.status)}
                    </div>
                    <CardDescription>
                      {format(new Date(incident.discovered_at), 'PPP')}
                    </CardDescription>
                    {/* Show linked entities */}
                    <div className="mt-2">
                      <EntityLinkedPreview entityType="incident" entityId={incident.id} maxDisplay={3} />
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDetailSheet(incident.id)}
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedIncidentId(incident.id);
                        setEditDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (incident.id) {
                          deleteIncident(incident.id);
                        }
                      }}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {(incident as any).owner && (
                    <div>
                      <p className="text-muted-foreground">Owner</p>
                      <p className="font-medium">{(incident as any).owner.name || (incident as any).owner.email}</p>
                    </div>
                  )}
                  {incident.impact && (
                    <div className="col-span-2">
                      <p className="text-muted-foreground">Impact</p>
                      <p className="font-medium">{incident.impact}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <IncidentCreateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={handleSubmit}
      />

      {/* Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selectedIncident && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between">
                  <SheetTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Incident Details
                  </SheetTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setDetailSheetOpen(false);
                        setEditDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={isDeleting}
                      onClick={() => {
                        if (selectedIncidentId) {
                          deleteIncident(selectedIncidentId);
                          setDetailSheetOpen(false);
                          setSelectedIncidentId(null);
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </SheetHeader>
              
              <div className="mt-6 space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">{selectedIncident.title}</h3>
                  <div className="flex items-center gap-4">
                    {getPriorityBadge(selectedIncident.priority)}
                    {getStatusBadge(selectedIncident.status)}
                  </div>
                </div>
                
                <div>
                  <p className="text-muted-foreground">Discovered</p>
                  <p className="font-medium">
                    {format(new Date(selectedIncident.discovered_at), 'PPP')}
                  </p>
                </div>
                
                {selectedIncident.impact && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Impact</h4>
                    <p className="text-sm text-muted-foreground">{selectedIncident.impact}</p>
                  </div>
                )}
                
                {selectedIncident.actions && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Actions Taken</h4>
                    <p className="text-sm text-muted-foreground">{selectedIncident.actions}</p>
                  </div>
                )}

                {selectedIncident.lessons_learned && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Lessons Learned</h4>
                    <p className="text-sm text-muted-foreground">{selectedIncident.lessons_learned}</p>
                  </div>
                )}
                
                <Separator />
                
                <div>
                  <div className="flex items-center gap-2 mb-4">
                    <Network className="h-4 w-4 text-muted-foreground" />
                    <h4 className="text-sm font-medium">Related Entities</h4>
                  </div>
                  <LinkManager
                    entityType="incident"
                    entityId={selectedIncident.id}
                    entityName={selectedIncident.title}
                    allowedTypes={['site', 'zone', 'system', 'endpoint', 'cloud_resource', 'risk', 'control', 'evidence', 'supplier', 'task']}
                    showHeader={false}
                  />
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <IncidentEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        incident={selectedIncident}
        onSave={(id, data) => updateIncident({ id, data })}
      />
    </div>
  );
}
